// =============================================================================
// src/features/auth/login-markup.ts — Markup da tela de login (gate de sessão).
//
// Layout portado do bloco shadcn "login-03" (coluna centrada: marca → card com
//   social + divisor + formulário → termos), re-vestido na identidade Observatory
//   (DESIGN.md): clay wash de fundo, card warm-white flat, azul institucional como
//   único accent. Provider social "gov.br" no lugar de Apple (contexto BR).
//   Fonte da estrutura: registry shadcn login-03 (ui.shadcn.com/r/styles/new-york/
//   login-03.json), consultado 2026-07-20 — replicado sem dependência de React.
//
// 2026-07-20 (redefinição de senha REAL): o card ganhou duas visões toggladas
//   por [hidden] — login (social + formulário) e reset (pedir código de 6
//   dígitos por e-mail → confirmar código + nova senha, via server/auth-*). A
//   nota de status subiu para o nível do card (serve às duas visões).
//
// SRP: só o HTML (string). Comportamento em login-takeover.ts; estilos em
//   src/styles/login.css.
// =============================================================================

import { resolveOwnerIdentity } from '../../app/owner-identity';
// per Context7 Vite asset docs (/vitejs/vite), 2026-06-26: ?url devolve a URL bundlada.
import loginBrandLogoUrl from '../../../assets/logo.svg?url';

/** Ids dos elementos interativos (fonte única p/ markup + login-takeover + testes). */
export const AUTH_LOGIN_IDS = {
  form: 'authLoginForm',
  email: 'authLoginEmail',
  password: 'authLoginPassword',
  submit: 'authLoginSubmit',
  google: 'authLoginGoogle',
  govbr: 'authLoginGovbr',
  forgot: 'authLoginForgot',
  requestAccess: 'authLoginRequestAccess',
  note: 'authLoginNote',
  title: 'authLoginTitle',
  subtitle: 'authLoginSubtitle',
  loginView: 'authLoginViewLogin',
  resetView: 'authLoginViewReset',
  resetEmail: 'authLoginResetEmail',
  resetRequest: 'authLoginResetRequest',
  resetStep2: 'authLoginResetStep2',
  resetCode: 'authLoginResetCode',
  resetNewPassword: 'authLoginResetNewPassword',
  resetConfirm: 'authLoginResetConfirm',
  resetBack: 'authLoginResetBack',
} as const;

/** Campo e-mail (autocomplete/inputmode reais; validação fica no takeover). */
const LOGIN_EMAIL_FIELD_MARKUP = `<div class="auth-login-field">
  <label class="auth-login-label" for="${AUTH_LOGIN_IDS.email}">E-mail institucional</label>
  <input class="auth-login-input" id="${AUTH_LOGIN_IDS.email}" name="email" type="email"
         autocomplete="email" placeholder="nome@r2tgroup.com" required />
</div>`;

/** Campo senha com o link "Esqueceu a senha?" na linha do rótulo (paridade login-03). */
const LOGIN_PASSWORD_FIELD_MARKUP = `<div class="auth-login-field">
  <div class="auth-login-label-row">
    <label class="auth-login-label" for="${AUTH_LOGIN_IDS.password}">Senha</label>
    <a class="auth-login-inline-link" id="${AUTH_LOGIN_IDS.forgot}" href="#">Esqueceu a senha?</a>
  </div>
  <input class="auth-login-input" id="${AUTH_LOGIN_IDS.password}" name="password" type="password"
         autocomplete="current-password" required />
</div>`;

/**
 * Subtítulo da visão de login (gabinete dinâmico) — fonte única usada pelo
 * markup inicial E pelo login-takeover ao voltar da visão de redefinição.
 *
 * @example buildLoginSubtitleText() // 'Acesse o observatório do gabinete Sen. Omar Aziz'
 */
export function buildLoginSubtitleText(): string {
  return `Acesse o observatório do gabinete ${resolveOwnerIdentity().name}`;
}

/** Marca acima do card: logo institucional + wordmark (paridade com o brand do login-03). */
function buildLoginBrandMarkup(): string {
  return `<div class="auth-login-brand">
    <img class="auth-login-brand-logo" src="${loginBrandLogoUrl}" alt="" aria-hidden="true" />
    <span class="auth-login-brand-name">Mapa Político</span>
  </div>`;
}

/** Botões sociais: vitrine do login-03 — NÃO autenticam (nota inline orienta). */
function buildLoginSocialMarkup(): string {
  return `<div class="auth-login-social">
    <button type="button" class="auth-login-social-btn" id="${AUTH_LOGIN_IDS.google}">
      <i class="ti ti-brand-google" aria-hidden="true"></i><span>Entrar com Google</span>
    </button>
    <button type="button" class="auth-login-social-btn" id="${AUTH_LOGIN_IDS.govbr}">
      <span>Entrar com <span class="auth-login-govbr-mark">gov.br</span></span>
    </button>
  </div>`;
}

/** Formulário e-mail/senha + CTA primário full-width (visão de login). */
function buildLoginFormMarkup(): string {
  return `<form class="auth-login-form" id="${AUTH_LOGIN_IDS.form}" novalidate>
    ${LOGIN_EMAIL_FIELD_MARKUP}
    ${LOGIN_PASSWORD_FIELD_MARKUP}
    <button type="submit" class="auth-login-submit" id="${AUTH_LOGIN_IDS.submit}">
      <i class="ti ti-login-2" aria-hidden="true"></i><span>Entrar</span>
    </button>
  </form>`;
}

/** Visão de login: social + divisor + formulário + pedido de acesso. */
function buildLoginViewMarkup(): string {
  return `<div class="auth-login-view" id="${AUTH_LOGIN_IDS.loginView}">
    ${buildLoginSocialMarkup()}
    <div class="auth-login-divider" role="separator"><span>Ou continue com e-mail</span></div>
    ${buildLoginFormMarkup()}
    <p class="auth-login-request">Ainda sem acesso?
      <a class="auth-login-inline-link" id="${AUTH_LOGIN_IDS.requestAccess}" href="#">Solicite ao administrador</a></p>
  </div>`;
}

/** Passo 2 da redefinição (revelado após o código ser enviado por e-mail). */
function buildResetStep2Markup(): string {
  return `<div class="auth-login-reset-step2" id="${AUTH_LOGIN_IDS.resetStep2}" hidden>
    <div class="auth-login-field">
      <label class="auth-login-label" for="${AUTH_LOGIN_IDS.resetCode}">Código recebido por e-mail</label>
      <input class="auth-login-input" id="${AUTH_LOGIN_IDS.resetCode}" name="codigo" type="text"
             inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="000000" />
    </div>
    <div class="auth-login-field">
      <label class="auth-login-label" for="${AUTH_LOGIN_IDS.resetNewPassword}">Nova senha</label>
      <input class="auth-login-input" id="${AUTH_LOGIN_IDS.resetNewPassword}" name="novaSenha" type="password"
             autocomplete="new-password" />
    </div>
    <button type="button" class="auth-login-submit" id="${AUTH_LOGIN_IDS.resetConfirm}">
      <i class="ti ti-shield-check" aria-hidden="true"></i><span>Redefinir senha</span>
    </button>
  </div>`;
}

/** Visão de redefinição de senha (pedir código → confirmar código + nova senha). */
function buildResetViewMarkup(): string {
  return `<div class="auth-login-view" id="${AUTH_LOGIN_IDS.resetView}" hidden>
    <div class="auth-login-field">
      <label class="auth-login-label" for="${AUTH_LOGIN_IDS.resetEmail}">E-mail cadastrado</label>
      <input class="auth-login-input" id="${AUTH_LOGIN_IDS.resetEmail}" name="resetEmail" type="email"
             autocomplete="email" placeholder="nome@r2tgroup.com" />
    </div>
    <button type="button" class="auth-login-submit" id="${AUTH_LOGIN_IDS.resetRequest}">
      <i class="ti ti-mail-forward" aria-hidden="true"></i><span>Enviar código</span>
    </button>
    ${buildResetStep2Markup()}
    <p class="auth-login-request"><a class="auth-login-inline-link" id="${AUTH_LOGIN_IDS.resetBack}" href="#">Voltar ao login</a></p>
  </div>`;
}

/** Card completo: cabeçalho (gabinete dinâmico) + nota + as duas visões. */
function buildLoginCardMarkup(): string {
  return `<section class="auth-login-card">
    <header class="auth-login-card-head">
      <h1 class="auth-login-title" id="${AUTH_LOGIN_IDS.title}">Bem-vindo de volta</h1>
      <p class="auth-login-subtitle" id="${AUTH_LOGIN_IDS.subtitle}">${buildLoginSubtitleText()}</p>
    </header>
    <p class="auth-login-note" id="${AUTH_LOGIN_IDS.note}" role="status" hidden></p>
    ${buildLoginViewMarkup()}
    ${buildResetViewMarkup()}
  </section>`;
}

/**
 * Devolve o HTML interno da tela de login (coluna centrada do login-03). O host
 * `.auth-login-takeover` é criado em runtime por login-takeover.ts (padrão do
 * settings-panel), então aqui vai só o conteúdo.
 *
 * @example byId('authLoginTakeover').innerHTML = buildLoginTakeoverMarkup();
 */
export function buildLoginTakeoverMarkup(): string {
  return `<div class="auth-login-column" role="dialog" aria-modal="true" aria-labelledby="${AUTH_LOGIN_IDS.title}">
    ${buildLoginBrandMarkup()}
    ${buildLoginCardMarkup()}
    <p class="auth-login-terms">Ao continuar, você concorda com os <a href="#">Termos de Uso</a>
      e a <a href="#">Política de Privacidade</a> do observatório.</p>
  </div>`;
}
